import { StandardEvents } from '@shopify/events';
import { morph } from '@theme/morph';

/**
 * Keeps Fresh Start's own markup in step with the selected variant.
 *
 * Horizon redraws a fixed set of elements on a variant change -- the gallery,
 * the price, the buy buttons, quantity rules -- and leaves everything else
 * showing the previous variant. That is fine for a stock theme, but the
 * Fresh Start product page adds its own subtitle, chips, description and
 * further-down sections whose photos and copy are variant-specific.
 *
 * The variant picker already fetches the entire product URL (no section_id is
 * appended on a product page), so the parsed response holds every section on
 * the page. Rather than refetching anything, this picks the Fresh Start
 * regions out of that same document and morphs them in.
 *
 * Mark a region with data-fsf-sync="<unique key>" and it stays in sync. The
 * key must be stable between renders, so it is derived from the section or
 * block id rather than from anything variant-specific.
 */

const SYNC_ATTR = 'data-fsf-sync';

/** @param {Event} event */
const handleProductSelect = (event) => {
  // Product cards run their own miniature variant swap. Only the main
  // product page's picker should redraw the page around it.
  if (event.target instanceof Element && event.target.closest('product-card')) return;

  const promise = /** @type {{ promise?: Promise<any> }} */ (event).promise;
  if (!promise) return;

  promise
    .then((result) => {
      const html = result?.detail?.html;
      if (!html) return;

      // A response carrying none of these regions is not a product render --
      // a redirect, a 404, or a preview whose token the fetch did not carry.
      // Without this guard every region below fails its lookup and is emptied
      // in the same pass, so one bad response blanks the price, the chips and
      // the pickers together and only a reload brings them back. Bailing
      // leaves the previous variant on screen: stale for a moment, not broken.
      if (!html.querySelector(`[${SYNC_ATTR}]`)) return;

      for (const current of document.querySelectorAll(`[${SYNC_ATTR}]`)) {
        const key = current.getAttribute(SYNC_ATTR);
        if (!key) continue;

        const next = html.querySelector(`[${SYNC_ATTR}="${CSS.escape(key)}"]`);

        if (next) {
          morph(current, next);

          // Server HTML never carries `is-visible`, so a morph strips it from
          // anything the scroll reveal had already shown, fading content the
          // reader is looking at back out. These elements are on screen by
          // definition, so show them straight away.
          for (const revealed of current.querySelectorAll('[data-fsf-reveal]')) {
            revealed.classList.add('is-visible');
          }
          if (current.hasAttribute('data-fsf-reveal')) current.classList.add('is-visible');
        } else {
          // The region renders nothing for this variant, so clear it rather
          // than leaving the previous variant's content sitting there.
          current.replaceChildren();
        }
      }
    })
    .catch(() => {
      // A superseded variant selection aborts its fetch. The newer selection
      // owns the redraw, so there is nothing to do here.
    });
};

// Capture phase, so this fires whether or not the event bubbles: the capture
// path always runs from the document down to the target.
document.addEventListener(StandardEvents.productSelect, handleProductSelect, { capture: true });
